import {Injectable} from '@angular/core';
import {ScheduleDay, ScheduleHour} from '../../models/Schedule';
import {isArray, isNumber} from 'util';
import {TranslateService} from '@ngx-translate/core';
import {Check} from '../../models/Check';
import * as moment from 'moment';
import {Reason} from '../../models/Reason';

@Injectable()
export class ScheduleService {

    constructor(public ts: TranslateService) {

    }

    public getTimeDelay(checks: Array<Check>, scheduleHour: Array<ScheduleHour>): number {
        if (checks === undefined || scheduleHour === undefined) {
            return 0;
        } else {
            return this.getTimeDelaySaveHours(checks, scheduleHour, null).delay;
        }
    }

    /**
     * @param {Array<Check>} checks array list with checks
     * @param {Array<ScheduleHour>} scheduleHour array list with Schedule hours
     * @param {Reason} reason if exist exceptional day
     * @returns {number} delay in milliseconds
     */
    public getTimeDelaySaveHours(checks: Array<Check>, scheduleHour: Array<ScheduleHour>, reason: Reason): any {
        if (!isArray(checks) || !isArray(scheduleHour)) {
            return 0;
        }
        let last = '';
        let outTime = 0;
        let inTime = 0;
        const hours: Array<ScheduleHour> = [];
        const checksUsed: Array<Check> = [];
        let inCheck: Check;
        let outCheck: Check;
        let work = 0;

        // console.log('->getTimeDelaySaveHours', checks, scheduleHour, reason);
        /**
         * Generamos parejas de Checks (IN <-> OUT)
         */
        checks.forEach(check => {
            if (check.event === 'IN' && last !== 'IN') {
                last = 'IN';
                inTime = check.date;
                inCheck = check;
            } else if (check.event === 'OUT' && last === 'IN') {
                last = 'OÜT';
                outTime = check.date;
                outCheck = check;
                checksUsed.push(inCheck, outCheck);
                work += (outTime - inTime);
                hours.push(new ScheduleHour(this.timeToString(inTime), this.timeToString(outTime)));
            }
        });
        // console.log('-----------------------------------------------------');
        // console.log('->Used: ', checksUsed);
        // console.log('----------', hours, scheduleHour);

        let delay = 0;
        /**
         * Cálculo de atrasos con la distancia minima de un Check(IN) con respecto al horario
         */
        scheduleHour.forEach(schedule => {
            hours.forEach(check => {
                const scheduleIN = this.stringHourToMillis(schedule.in);
                const scheduleOUT = this.stringHourToMillis(schedule.out);
                const checkIN = this.stringHourToMillis(check.in);
                const checkOUT = this.stringHourToMillis(check.out);

                if (this.intercept(scheduleIN, scheduleOUT, checkIN, checkOUT) && checkIN - scheduleIN > 0) { // if exist delay
                    let periodDelay = checkIN - scheduleIN;
                    if (reason != null) {
                        const reasonIN = this.stringHourToMillis(reason.in);
                        const reasonOUT = this.stringHourToMillis(reason.out);
                        if (this.intercept(reasonIN, reasonOUT, scheduleIN, scheduleOUT)) {// tolerance time
                            const tolerance = this.overlap(reasonIN, reasonOUT, scheduleIN, scheduleOUT);
                            periodDelay = (periodDelay - tolerance > 0) ? (periodDelay - tolerance) : 0;
                        }
                    }
                    delay += periodDelay;
                }
            });
        });
        // console.log('-> Total delay:', this.millisToStringVerbose(delay));
        return {
            delay: delay,
            work: work,
            checksUsed: checksUsed
        };
    }

    public intercept(in1: number, out1: number, in2: number, out2: number): boolean {
        return Math.max(-1, Math.min(out1, out2) - Math.max(in1, in2)) >= 0;
    }

    public overlap(in1: number, out1: number, in2: number, out2: number): number {
        let over = Math.max(-1, Math.min(out1, out2) - Math.max(in1, in2));
        if (over < 0) over = 0;
        return over;
    }

    public stringHourToMillis(hours: string) {
        if (!hours) {
            return 0;
        }
        return parseInt(hours.split(':')[0]) * 60 * 60 * 1000 +
            parseInt(hours.split(':')[1]) * 60 * 1000;
    }

    public millisToStringVerbose(millis: number): string {
        if (!isNumber(millis)) {
            return '0 Min';
        }
        const sec = '0' + Math.floor(millis / 1000) % 60;
        const min = '0' + Math.floor(millis / 1000 / 60) % 60;
        const hrs = Math.floor(millis / 1000 / 60 / 60) % 24;
        if (hrs > 0) {
            return ('0' + hrs).substr(-2) + ' Hr(s), ' + min.substr(-2) + ' Min(s)';
        } else {
            return min.substr(-2) + ' Min(s)';
        }
    }

    public millisToStringDaysVerbose(millis: number): string {
        const sec = '0' + Math.floor(millis / 1000) % 60;
        const min = '0' + Math.floor(millis / 1000 / 60) % 60;
        const hrs = '0' + Math.floor(millis / 1000 / 60 / 60) % 24;
        const days = Math.floor(millis / 1000 / 60 / 60 / 24);
        if (days > 0) {
            return days + ' day(s) ' + hrs.substr(-2) + ' Hr(s), ' + min.substr(-2) + ' Min(s)';
        } else {
            return hrs.substr(-2) + ' Hr(s), ' + min.substr(-2) + ' Min(s)';
        }
    }

    public millisToSimpleStringVerbose(millis: number): string {
        const sec = '0' + Math.floor(millis / 1000) % 60;
        const min = '0' + Math.floor(millis / 1000 / 60) % 60;
        const hrs = '0' + Math.floor(millis / 1000 / 60 / 60) % 24;
        return hrs.substr(-2) + '.' + min.substr(-2);
    }

    /**
     * @return timestamp to string hour in format HH:mm
     */
    public timeToString(time: number): string {
        return moment(time).format('HH:mm');
    }

    /**
     * Return verbose Schedule in format HTML
     * @param {Array<ScheduleDay>} scheduleDays
     * @returns {string} verbose html display schedule:
     * Example: "Lun,Mar,Mie,Vie (08:30-12:30/14:00-18:00)<br>Jue (08:30-12:30)"
     */
    public generateVerboseSchedule(scheduleDays: Array<ScheduleDay>): string {
        if (!isArray(scheduleDays)) {
            return '';
        }
        let groups: Array<any> = this.separateByHourGroups(scheduleDays);
        let verboseList: any[] = [];
        groups.forEach(group => {
            let verboseGroup: any[] = [];
            group.forEach(scheduleDay => {
                this.ts.get(scheduleDay.dayAbbr).subscribe(trans => verboseGroup.push(trans));
            });
            verboseList.push(verboseGroup.join(',') + ' (<span class="small">' + ScheduleDay.toString(group[0].hours) + '</span>)');
        });
        return verboseList.join(' <br> ');
    }

    public generateSimpleVerboseSchedule(scheduleDays: Array<ScheduleDay>): string {
        if (!isArray(scheduleDays)) {
            return '';
        }
        let groups: Array<any> = this.separateByHourGroups(scheduleDays);
        let verboseList: any[] = [];
        groups.forEach(group => {
            let verboseGroup: any[] = [];
            group.forEach(scheduleDay => {
                this.ts.get(scheduleDay.dayAbbr).subscribe(trans => verboseGroup.push(trans));
            });
            verboseList.push(verboseGroup.join(',') + ' (' + ScheduleDay.toString(group[0].hours) + ')');
        });
        return verboseList.join(' \n ');
    }

    /**
     * @param date in format yyyy-MM-dd
     * @return number of day between 0 to 6 (sunday, friday)
     */
    public getDayFromDate(date): number {
        const day = moment(date).day() - 1;
        return day < 0 ? 6 : day; // if day is sunday then day is 6
    }

    /**
     * @param {Array<ScheduleDay>} scheduleDays
     * @returns {Array<any>} positions contains list with same dayHourSchedule
     */
    public separateByHourGroups(scheduleDays: Array<ScheduleDay>): Array<any> {
        let group: Array<any> = [];
        if (scheduleDays.length === 0) {
            return [];
        }
        for (let i = 0; i < scheduleDays.length; i++) {
            if (scheduleDays[i].hours && scheduleDays[i].hours.length > 0) {
                let indexExist = -1;
                for (let j = 0; j < group.length; j++) {
                    if (this.compareHours(scheduleDays[i].hours, group[j][0].hours)) {
                        indexExist = j;
                        break;
                    }
                }
                if (indexExist !== -1) {
                    group[indexExist].push(scheduleDays[i]);
                } else {
                    group.push([scheduleDays[i]]);
                }
            }
        }
        return group;
    }

    /**
     * @param {Array<ScheduleHour>} old
     * @param {Array<ScheduleHour>} current
     * @returns {boolean} if day schedule is equals
     */
    private compareHours(old: Array<ScheduleHour>, current: Array<ScheduleHour>): boolean {
        let tempEqual = true;
        if (old.length !== current.length) {
            return false;
        }
        for (let k = 0; k < old.length; k++) {
            if (old[k].in !== current[k].in ||
                old[k].out !== current[k].out) {
                tempEqual = false;
                break;
            }
        }
        return tempEqual;
    }

    splitPerDay(checks: Array<Check>): Array<any> {
        const _dayCheck = [];
        const dayCheck = [];
        checks.forEach(check => {
                const date: string = moment(check.date).format('YYYY-MM-DD');
                if (!_dayCheck[date]) {
                    _dayCheck[date] = [];
                }
                _dayCheck[date].push(check);
            }
        );

        for (let day in _dayCheck) {
            dayCheck.push({
                day: day,
                checks: _dayCheck[day]
            });
        }
        return dayCheck;
    }

    public getReasonByDate(reasons: Array<Reason>, day: any): Reason {
        const finalDay = moment(day).format('MM-DD-YYYY');
        let reasonMatch = null;
        reasons.forEach(reason => {
            const tmpDay = moment(reason.date).format('MM-DD-YYYY');
            if (tmpDay === finalDay) {
                reasonMatch = reason;
            }
        });
        return reasonMatch;
    }
}



// WEBPACK FOOTER //
// ./src/app/services/ScheduleService.ts